import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import { fmtLocalDTFull } from "@/lib/clinical";
import {
  ReceiptText, Wallet, PlusCircle, PiggyBank, Percent, Ban, RefreshCw, Trash2,
  FileText, Printer, CheckCircle2, RotateCcw, Pencil, ShieldCheck,
} from "lucide-react";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n || 0));

const KIND_LABEL = { advance: "Advance", payment: "Payment", refund: "Refund" };
const METHOD_LABEL = { cash: "Cash", upi: "UPI", card: "Card", bank: "Bank Transfer", insurance: "Insurance", credit: "Credit" };

export default function BillingTab({ patient }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [charges, setCharges] = useState([]);
  const [openItem, setOpenItem] = useState(false);
  const [openPayment, setOpenPayment] = useState(false);
  const [openDiscount, setOpenDiscount] = useState(false);
  const [openVoid, setOpenVoid] = useState(false);
  const [paymentKind, setPaymentKind] = useState("advance");
  const [editItem, setEditItem] = useState(null);

  const isAdmin = user?.role === "admin";
  const canBill = ["admin", "doctor", "nurse", "receptionist"].includes(user?.role);
  const canRemove = (item) => {
    if (isVoid) return false;
    // Admin bypass — can remove items on closed bills too. Others need bill.status = 'open'.
    if (!isAdmin && bill?.status !== "open") return false;
    if (item.source === "auto" && !isAdmin) return false;
    return canBill;
  };
  const removePayment = async (paymentId) => {
    if (!window.confirm("Delete this payment/receipt? This is audited.")) return;
    try {
      await api.delete(`/billing/bills/payments/${paymentId}`);
      toast.success("Payment removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeDiscount = async (discountId) => {
    if (!window.confirm("Remove this discount?")) return;
    try {
      await api.delete(`/billing/bills/discounts/${discountId}`);
      toast.success("Discount removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/billing/bills/${patient.id}`);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load bill");
    } finally {
      setLoading(false);
    }
  }, [patient.id]);

  const loadCharges = useCallback(async () => {
    try {
      const { data: cs } = await api.get(`/billing/charges?active_only=true`);
      setCharges(cs || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); loadCharges(); }, [load, loadCharges]);

  const bill = data?.bill;
  const items = data?.items || [];
  const payments = data?.payments || [];
  const discounts = data?.discounts || [];
  const totals = data?.totals || {};
  const excludedKeys = bill?.excluded_charge_keys || [];

  const restoreCharge = async (chargeKey) => {
    if (!isAdmin || !bill?.id) return;
    try {
      await api.post(`/billing/bills/${bill.id}/include-charge/${encodeURIComponent(chargeKey)}`);
      toast.success(`"${chargeKey}" restored`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const grouped = useMemo(() => {
    // Aggregate auto-generated recurring charges (bed / equipment / oxygen /
    // doctor visit / nursing) into ONE row per charge_key so the bill shows
    // "NICU L2 bed × 5 days" instead of 5 dated rows. Manual charges and
    // one-off procedures keep their individual rows.
    const AUTO_ROLLUP = new Set([
      "bed_l2", "bed_l3", "bed_iso",
      "ventilator", "cpap", "hfnc", "oxygen", "phototherapy",
      "doctor_visit", "nursing",
    ]);
    const g = {};
    const rolled = {};
    items.forEach((i) => {
      const isRollup = i.source === "auto" && AUTO_ROLLUP.has(i.charge_key);
      if (isRollup) {
        const bucket = rolled[i.charge_key];
        if (!bucket) {
          rolled[i.charge_key] = {
            ...i,
            id: `rollup-${i.charge_key}`,
            _rollup: true,
            _rollup_of: 1,
            qty: Number(i.qty || 0),
            amount: Number(i.amount || 0),
            gst_amount: Number(i.gst_amount || 0),
            total: Number(i.total || 0),
            day_key: i.day_key ? `${i.day_key} … ${i.day_key}` : "—",
            _from: i.day_key || null,
            _to: i.day_key || null,
          };
        } else {
          bucket.qty += Number(i.qty || 0);
          bucket.amount += Number(i.amount || 0);
          bucket.gst_amount += Number(i.gst_amount || 0);
          bucket.total += Number(i.total || 0);
          bucket._rollup_of += 1;
          if (i.day_key && (!bucket._from || i.day_key < bucket._from)) bucket._from = i.day_key;
          if (i.day_key && (!bucket._to || i.day_key > bucket._to)) bucket._to = i.day_key;
          bucket.day_key = `${bucket._from} … ${bucket._to}`;
        }
        return;
      }
      const k = i.category || "misc";
      if (!g[k]) g[k] = [];
      g[k].push(i);
    });
    // Insert rolled-up rows back under their category
    Object.values(rolled).forEach((r) => {
      const k = r.category || "misc";
      if (!g[k]) g[k] = [];
      g[k].unshift(r);
    });
    return g;
  }, [items]);

  const isVoid = bill?.status === "void";
  const adminEditClosed = isAdmin && bill?.status === "closed";

  const removeItem = async (id) => {
    if (!window.confirm("Remove this line item?")) return;
    try {
      await api.delete(`/billing/bills/items/${id}`);
      toast.success("Item removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeRollup = async (item) => {
    if (!isAdmin || !bill?.id) return;
    const msg = `Delete all ${item._rollup_of} auto-billed "${item.name}" entries from this bill?\n\nThis removes every day-row for this charge (${item.day_key}). Historical bills you've already printed keep their line items.`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.delete(
        `/billing/bills/${bill.id}/items/bulk?charge_key=${encodeURIComponent(item.charge_key)}&source=auto`,
      );
      toast.success(`Removed ${data.deleted} rows`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openBillPdf = async (finalize = false) => {
    try {
      const res = await api.get(
        `/billing/bills/${patient.id}/invoice.pdf${finalize ? "?finalize=true" : ""}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      if (finalize) load();
    } catch (e) {
      const err = e?.response?.data;
      if (err instanceof Blob) {
        const text = await err.text();
        toast.error(text || "Failed to open invoice");
      } else {
        toast.error(err?.detail || "Failed to open invoice");
      }
    }
  };

  const openReceiptPdf = async (receiptNo) => {
    try {
      const res = await api.get(
        `/billing/bills/${patient.id}/receipts/${encodeURIComponent(receiptNo)}.pdf`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { toast.error("Failed to open receipt"); }
  };

  if (loading || !data) return <div className="text-sm text-muted-foreground">Loading bill…</div>;

  return (
    <div className="space-y-6" data-testid="billing-tab">
      {/* Header stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Grand Total" value={inr(totals.grand_total)} tone="default" />
        <StatCard label="Received" value={inr((totals.advance_total || 0) + (totals.paid_total || 0) - (totals.refund_total || 0))} tone="ok" />
        <StatCard label="Discount" value={inr(totals.discount_total)} tone="muted" />
        <StatCard label="Balance Due" value={inr(totals.balance_due)} tone={totals.balance_due > 0 ? "warn" : "ok"} />
      </div>

      {isVoid && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm">
          <strong>This bill has been voided.</strong> Reason: {bill.void_reason || "—"} · by {bill.void_by} on {fmtLocalDTFull(bill.void_at)}
        </div>
      )}

      {adminEditClosed && (
        <div className="rounded-lg border border-primary/40 bg-primary/5 px-4 py-3 text-sm flex items-center gap-2" data-testid="admin-closed-edit-banner">
          <ShieldCheck size={15} className="text-primary" />
          <span><strong>Finalized bill — admin edit mode.</strong> You can add, edit or remove charges, payments and discounts. Invoice {bill.invoice_no} keeps its number; reprint it after changes. All edits are audited.</span>
        </div>
      )}

      {/* Action bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
        <div className="text-xs text-muted-foreground">
          Bill opened {fmtLocalDTFull(bill.opened_at)} · Status <Badge className="ml-1 rounded-full text-[10px]">{bill.status}</Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="rounded-full" onClick={load} data-testid="btn-billing-refresh">
            <RefreshCw size={13} className="mr-1.5" /> Refresh
          </Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => openBillPdf(false)} data-testid="btn-preview-invoice">
            <FileText size={13} className="mr-1.5" /> {bill.invoice_no ? "Print invoice" : "Preview invoice"}
          </Button>
          {canBill && !isVoid && !bill.invoice_no && (
            <Button size="sm" className="rounded-full" onClick={() => openBillPdf(true)} data-testid="btn-finalize-invoice">
              <CheckCircle2 size={13} className="mr-1.5" /> Finalize &amp; print
            </Button>
          )}
          {canBill && !isVoid && (bill.status === "open" || adminEditClosed) && (
            <Button size="sm" className="rounded-full" onClick={() => setOpenItem(true)} data-testid="btn-add-item">
              <PlusCircle size={13} className="mr-1.5" /> Add procedure / charge
            </Button>
          )}
          {canBill && !isVoid && (bill.status !== "closed" || isAdmin) && (
            <Button size="sm" className="rounded-full" onClick={() => setOpenPayment(true)} data-testid="btn-add-payment">
              <Wallet size={13} className="mr-1.5" /> Record payment / advance
            </Button>
          )}
          {canBill && !isVoid && bill.status === "closed" && (totals.advance_total + totals.paid_total > 0) && (
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setPaymentKind("refund"); setOpenPayment(true); }} data-testid="btn-refund">
              <RotateCcw size={13} className="mr-1.5" /> Refund
            </Button>
          )}
          {isAdmin && !isVoid && (
            <>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => setOpenDiscount(true)} data-testid="btn-add-discount">
                <Percent size={13} className="mr-1.5" /> Discount
              </Button>
              <Button size="sm" variant="destructive" className="rounded-full" onClick={() => setOpenVoid(true)} data-testid="btn-void-bill">
                <Ban size={13} className="mr-1.5" /> Void bill
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Items */}
      <div className="card-clinical p-5">
        <div className="flex items-center gap-2 mb-4">
          <ReceiptText size={14} className="text-primary" />
          <span className="text-xs uppercase tracking-widest text-muted-foreground">Charges ({items.length})</span>
        </div>
        {isAdmin && excludedKeys.length > 0 && (
          <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3" data-testid="excluded-charges-banner">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-medium text-amber-700 dark:text-amber-400">Auto-billing excluded on this bill:</span>
              {excludedKeys.map((k) => (
                <button
                  key={k}
                  onClick={() => restoreCharge(k)}
                  className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-background px-2.5 py-1 font-mono text-[11px] hover:border-emerald-500/50 hover:text-emerald-600"
                  title="Click to restore this auto-billing charge on this bill"
                  data-testid={`restore-${k}`}
                >
                  <RotateCcw size={11} /> {k}
                </button>
              ))}
              <span className="text-muted-foreground">Click any tag to restore.</span>
            </div>
          </div>
        )}
        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground">No charges yet.</div>
        ) : (
          <div className="space-y-4">
            {Object.entries(grouped).map(([cat, arr]) => (
              <div key={cat}>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">{cat}</div>
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-widest text-muted-foreground">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Item</th>
                        <th className="text-left px-3 py-2 font-medium">Day</th>
                        <th className="text-right px-3 py-2 font-medium">Qty</th>
                        <th className="text-right px-3 py-2 font-medium">Rate</th>
                        <th className="text-right px-3 py-2 font-medium">GST %</th>
                        <th className="text-right px-3 py-2 font-medium">Total</th>
                        <th className="text-right px-3 py-2 font-medium w-16"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {arr.map((i) => (
                        <tr key={i.id} className="border-t border-border">
                          <td className="px-3 py-2">
                            {i.name}
                            {i._rollup && <Badge variant="outline" className="ml-2 rounded-full text-[9px]">rolled up · {i._rollup_of} days</Badge>}
                            {!i._rollup && i.source === "auto" && <Badge variant="outline" className="ml-2 rounded-full text-[9px]">auto</Badge>}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground tabular text-[12px]">{i.day_key || "—"}</td>
                          <td className="px-3 py-2 text-right tabular">{i.qty}</td>
                          <td className="px-3 py-2 text-right tabular">{inr(i.rate)}</td>
                          <td className="px-3 py-2 text-right tabular">{i.gst_percent || 0}</td>
                          <td className="px-3 py-2 text-right tabular font-medium">{inr(i.total)}</td>
                          <td className="px-3 py-2 text-right">
                            {i._rollup && isAdmin && !isVoid && (
                              <button className="text-muted-foreground hover:text-destructive" onClick={() => removeRollup(i)} title={`Delete all ${i._rollup_of} auto rows`} data-testid={`del-rollup-${i.charge_key}`}>
                                <Trash2 size={13} />
                              </button>
                            )}
                            {!i._rollup && isAdmin && !isVoid && (
                              <button className="text-muted-foreground hover:text-primary mr-2" onClick={() => setEditItem(i)} title="Edit (admin)" data-testid={`edit-item-${i.id}`}>
                                <Pencil size={13} />
                              </button>
                            )}
                            {!i._rollup && canRemove(i) && (
                              <button className="text-muted-foreground hover:text-destructive" onClick={() => removeItem(i.id)} title="Remove" data-testid={`del-item-${i.id}`}>
                                <Trash2 size={13} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            <div className="flex justify-end pt-2">
              <div className="w-full max-w-sm text-sm space-y-1">
                <Row label="Sub-total" value={inr(totals.charges_total)} />
                <Row label="GST" value={inr(totals.gst_total)} />
                <Row label="Discount" value={`- ${inr(totals.discount_total)}`} muted={!totals.discount_total} />
                <div className="border-t border-border my-1" />
                <Row label="Net payable" value={inr(totals.net_payable)} bold />
                <Row label="Advance / paid" value={`- ${inr(totals.advance_total + totals.paid_total)}`} muted />
                {totals.refund_total ? <Row label="Refunds" value={`+ ${inr(totals.refund_total)}`} /> : null}
                <div className="border-t border-border my-1" />
                <Row label="Balance due" value={inr(totals.balance_due)} bold tone={totals.balance_due > 0 ? "warn" : "ok"} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payments */}
      <div className="card-clinical p-5">
        <div className="flex items-center gap-2 mb-4">
          <PiggyBank size={14} className="text-primary" />
          <span className="text-xs uppercase tracking-widest text-muted-foreground">Payments &amp; advances ({payments.length})</span>
        </div>
        {payments.length === 0 ? (
          <div className="text-sm text-muted-foreground">No payments recorded.</div>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-[11px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Receipt</th>
                  <th className="text-left px-3 py-2 font-medium">Date</th>
                  <th className="text-left px-3 py-2 font-medium">Kind</th>
                  <th className="text-left px-3 py-2 font-medium">Method</th>
                  <th className="text-left px-3 py-2 font-medium">Reference</th>
                  <th className="text-left px-3 py-2 font-medium">By</th>
                  <th className="text-right px-3 py-2 font-medium">Amount</th>
                  <th className="text-right px-3 py-2 font-medium w-10"></th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="px-3 py-2 tabular text-[12px]">{p.receipt_no}</td>
                    <td className="px-3 py-2 tabular text-[12px]">{fmtLocalDTFull(p.at)}</td>
                    <td className="px-3 py-2">
                      <Badge variant={p.kind === "refund" ? "destructive" : "outline"} className="rounded-full text-[10px]">
                        {KIND_LABEL[p.kind]}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">{METHOD_LABEL[p.method] || p.method}</td>
                    <td className="px-3 py-2 text-muted-foreground text-[12px]">{p.reference || "—"}</td>
                    <td className="px-3 py-2 text-muted-foreground text-[12px]">{p.taken_by}</td>
                    <td className={`px-3 py-2 text-right tabular font-medium ${p.kind === "refund" ? "text-destructive" : ""}`}>
                      {p.kind === "refund" ? "-" : ""}{inr(p.amount)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button className="text-muted-foreground hover:text-primary" onClick={() => openReceiptPdf(p.receipt_no)} title="Print receipt" data-testid={`print-receipt-${p.receipt_no}`}>
                          <Printer size={13} />
                        </button>
                        {isAdmin && (
                          <button className="text-muted-foreground hover:text-destructive" onClick={() => removePayment(p.id)} title="Delete payment (admin)" data-testid={`del-payment-${p.id}`}>
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Discounts */}
      {discounts.length > 0 && (
        <div className="card-clinical p-5">
          <div className="flex items-center gap-2 mb-4">
            <Percent size={14} className="text-primary" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Discounts ({discounts.length})</span>
          </div>
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-[11px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Date</th>
                  <th className="text-left px-3 py-2 font-medium">Kind</th>
                  <th className="text-right px-3 py-2 font-medium">Value</th>
                  <th className="text-left px-3 py-2 font-medium">Reason</th>
                  <th className="text-left px-3 py-2 font-medium">Approved by</th>
                  <th className="text-right px-3 py-2 font-medium w-8"></th>
                </tr>
              </thead>
              <tbody>
                {discounts.map((d) => (
                  <tr key={d.id} className="border-t border-border">
                    <td className="px-3 py-2 tabular text-[12px]">{fmtLocalDTFull(d.at)}</td>
                    <td className="px-3 py-2">{d.kind === "percent" ? "%" : "Fixed"}</td>
                    <td className="px-3 py-2 text-right tabular">{d.kind === "percent" ? `${d.value}%` : inr(d.value)}</td>
                    <td className="px-3 py-2">{d.reason}</td>
                    <td className="px-3 py-2 text-muted-foreground text-[12px]">{d.approved_by}</td>
                    <td className="px-3 py-2 text-right">
                      {isAdmin && (
                        <button className="text-muted-foreground hover:text-destructive" onClick={() => removeDiscount(d.id)} title="Remove discount (admin)" data-testid={`del-discount-${d.id}`}>
                          <Trash2 size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {openItem && <AddItemModal patient={patient} charges={charges} onClose={() => setOpenItem(false)} onSaved={async () => { await load(); setOpenItem(false); }} />}
      {openPayment && <AddPaymentModal patient={patient} balance={totals.balance_due} initialKind={paymentKind} onClose={() => { setOpenPayment(false); setPaymentKind("advance"); }} onSaved={async () => { await load(); setOpenPayment(false); setPaymentKind("advance"); }} />}
      {openDiscount && <AddDiscountModal patient={patient} onClose={() => setOpenDiscount(false)} onSaved={async () => { await load(); setOpenDiscount(false); }} />}
      {editItem && <EditItemModal item={editItem} onClose={() => setEditItem(null)} onSaved={async () => { await load(); setEditItem(null); }} />}
      {openVoid && <VoidBillModal bill={bill} onClose={() => setOpenVoid(false)} onSaved={async () => { await load(); setOpenVoid(false); }} />}
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const cls =
    tone === "warn" ? "text-amber-600 dark:text-amber-400"
    : tone === "ok" ? "text-emerald-600 dark:text-emerald-400"
    : tone === "muted" ? "text-muted-foreground"
    : "";
  return (
    <div className="card-clinical p-4">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-display font-semibold text-2xl mt-1 tabular ${cls}`}>{value}</div>
    </div>
  );
}

function Row({ label, value, bold, muted, tone }) {
  const cls =
    tone === "warn" ? "text-amber-600 dark:text-amber-400"
    : tone === "ok" ? "text-emerald-600 dark:text-emerald-400"
    : muted ? "text-muted-foreground"
    : "";
  return (
    <div className={`flex justify-between ${bold ? "font-semibold" : ""}`}>
      <span className="text-muted-foreground">{label}</span>
      <span className={`tabular ${cls}`}>{value}</span>
    </div>
  );
}

function AddItemModal({ patient, charges, onClose, onSaved }) {
  const [mode, setMode] = useState("catalog");
  const [chargeId, setChargeId] = useState("");
  const [qty, setQty] = useState(1);
  const [name, setName] = useState("");
  const [rate, setRate] = useState("");
  const [gst, setGst] = useState("");
  const [category, setCategory] = useState("procedure");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const selectable = charges.filter((c) => c.category !== "bed" && c.category !== "admission");

  const save = async () => {
    setSaving(true);
    try {
      const body = mode === "catalog"
        ? { charge_id: chargeId, qty: Number(qty), notes }
        : { name, rate: Number(rate), gst_percent: Number(gst || 0), qty: Number(qty), category, notes };
      await api.post(`/billing/bills/${patient.id}/items`, body);
      toast.success("Charge added");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="add-item-modal">
        <DialogHeader>
          <DialogTitle>Add procedure / charge</DialogTitle>
          <DialogDescription>Pick from the charge master or enter a one-off custom charge.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 mb-3">
          <button className={`rounded-full px-3 py-1 text-xs border ${mode === "catalog" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
                  onClick={() => setMode("catalog")}>From catalog</button>
          <button className={`rounded-full px-3 py-1 text-xs border ${mode === "custom" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
                  onClick={() => setMode("custom")}>Custom</button>
        </div>
        {mode === "catalog" ? (
          <div className="space-y-3">
            <div>
              <Label>Charge</Label>
              <Select value={chargeId} onValueChange={setChargeId}>
                <SelectTrigger data-testid="sel-charge"><SelectValue placeholder="Pick a charge" /></SelectTrigger>
                <SelectContent>
                  {selectable.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {inr(c.rate)} ({c.category})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantity</Label>
              <Input type="number" min="1" step="1" value={qty} onChange={(e) => setQty(e.target.value)} />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="ci-name" /></div>
            <div><Label>Rate (₹)</Label><Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="ci-rate" /></div>
            <div><Label>GST %</Label><Input type="number" value={gst} onChange={(e) => setGst(e.target.value)} /></div>
            <div><Label>Qty</Label><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} /></div>
            <div>
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="procedure">Procedure</SelectItem>
                  <SelectItem value="investigation">Investigation</SelectItem>
                  <SelectItem value="equipment">Equipment</SelectItem>
                  <SelectItem value="misc">Miscellaneous</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || (mode === "catalog" ? !chargeId : (!name || !rate))} data-testid="btn-save-item">
            {saving ? "Saving…" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddPaymentModal({ patient, balance, initialKind, onClose, onSaved }) {
  const [kind, setKind] = useState(initialKind || "advance");
  const [method, setMethod] = useState("cash");
  const [amount, setAmount] = useState(balance > 0 ? balance.toFixed(2) : "");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!amount || Number(amount) <= 0) return toast.error("Enter a positive amount");
    setSaving(true);
    try {
      await api.post(`/billing/bills/${patient.id}/payments`, {
        kind, method, amount: Number(amount), reference, notes,
      });
      toast.success("Payment recorded");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="add-payment-modal">
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
          <DialogDescription>Balance due: <strong>{inr(balance)}</strong></DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Kind</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="sel-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="advance">Advance</SelectItem>
                <SelectItem value="payment">Payment</SelectItem>
                <SelectItem value="refund">Refund</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="sel-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="bank">Bank Transfer</SelectItem>
                <SelectItem value="insurance">Insurance</SelectItem>
                <SelectItem value="credit">Credit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Amount (₹)</Label>
            <Input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="ci-amount" />
          </div>
          <div>
            <Label>Reference / Txn #</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label>Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="btn-save-payment">{saving ? "Saving…" : "Record"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddDiscountModal({ patient, onClose, onSaved }) {
  const [kind, setKind] = useState("percent");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!reason.trim()) return toast.error("Reason is mandatory");
    if (!value || Number(value) <= 0) return toast.error("Value must be positive");
    setSaving(true);
    try {
      await api.post(`/billing/bills/${patient.id}/discounts`, { kind, value: Number(value), reason });
      toast.success("Discount applied");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="add-discount-modal">
        <DialogHeader>
          <DialogTitle>Apply discount</DialogTitle>
          <DialogDescription>Admin only. Reason is required and logged in the audit trail.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Kind</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="percent">Percent (%)</SelectItem>
                <SelectItem value="fixed">Fixed amount (₹)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Value</Label>
            <Input type="number" min="0.01" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} data-testid="ci-discount-value" />
          </div>
          <div>
            <Label>Reason *</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="ci-discount-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="btn-save-discount">{saving ? "Saving…" : "Apply"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditItemModal({ item, onClose, onSaved }) {
  const [name, setName] = useState(item.name || "");
  const [qty, setQty] = useState(item.qty ?? 1);
  const [rate, setRate] = useState(item.rate ?? 0);
  const [gst, setGst] = useState(item.gst_percent ?? 0);
  const [notes, setNotes] = useState(item.notes || "");
  const [saving, setSaving] = useState(false);
  const preview = Number(qty || 0) * Number(rate || 0) * (1 + Number(gst || 0) / 100);

  const save = async () => {
    if (!(Number(qty) > 0)) return toast.error("Quantity must be positive");
    if (rate === "" || Number(rate) < 0) return toast.error("Enter a valid rate");
    setSaving(true);
    try {
      await api.patch(`/billing/bills/items/${item.id}`, { name, qty: Number(qty), rate: Number(rate), gst_percent: Number(gst || 0), notes });
      toast.success("Charge updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="edit-item-modal">
        <DialogHeader>
          <DialogTitle>Edit charge</DialogTitle>
          <DialogDescription>Admin only. The change is recorded in the billing audit log.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-3"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="ei-name" /></div>
          <div><Label>Qty</Label><Input type="number" min="0.01" step="0.01" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="ei-qty" /></div>
          <div><Label>Rate (₹)</Label><Input type="number" min="0" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="ei-rate" /></div>
          <div><Label>GST %</Label><Input type="number" min="0" step="0.01" value={gst} onChange={(e) => setGst(e.target.value)} data-testid="ei-gst" /></div>
          <div className="col-span-3"><Label>Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          <div className="col-span-3 text-sm text-muted-foreground">New total: <strong className="text-foreground tabular" data-testid="ei-total">{inr(preview)}</strong></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="btn-save-edit-item">{saving ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VoidBillModal({ bill, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!reason.trim()) return toast.error("Reason is mandatory");
    setSaving(true);
    try {
      await api.post(`/billing/bills/${bill.id}/void`, { reason });
      toast.success("Bill voided");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="void-bill-modal">
        <DialogHeader>
          <DialogTitle>Void this bill?</DialogTitle>
          <DialogDescription>The bill becomes read-only and stops accruing. This is logged.</DialogDescription>
        </DialogHeader>
        <div>
          <Label>Reason *</Label>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="ci-void-reason" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="destructive" onClick={save} disabled={saving} data-testid="btn-save-void">Void</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
